// A machine-readable résumé built only from the site's own content, in the
// JSON Resume format (https://jsonresume.org/schema) that many résumé and
// recruiting tools can import.
import { contactsData } from '../data/contactsData';
import { certificationsData, educationData } from '../data/educationData';
import { experienceData } from '../data/experienceData';
import { headerData } from '../data/headerData';
import { projectsData } from '../data/projectsData';
import { skillsData } from '../data/skillsData';

export const SITE_URL = 'https://miretu.vercel.app';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

// "Aug 2024" -> "2024-08", "2023" -> "2023", "Present"/"" -> undefined.
export function toIsoDate(text) {
    const value = String(text || '').trim();
    if (!value || /^present$/i.test(value)) return undefined;
    const monthYear = value.match(/^([a-z]{3})[a-z]*\.?\s+(\d{4})$/i);
    if (monthYear) {
        const month = MONTHS.indexOf(monthYear[1].toLowerCase());
        if (month >= 0) return `${monthYear[2]}-${String(month + 1).padStart(2, '0')}`;
    }
    return /^\d{4}$/.test(value) ? value : undefined;
}

// Drops keys that are empty so the output carries no placeholders.
const compact = (object) =>
    Object.fromEntries(
        Object.entries(object).filter(([, value]) => {
            if (value === undefined || value === null || value === '') return false;
            return !(Array.isArray(value) && value.length === 0);
        })
    );

const splitPlace = (address = '') => {
    const [city, country] = address.split(',').map((part) => part.trim());
    return { city, country };
};

// "Name, Issuer" -> { name, issuer } (splits on the last comma).
const splitCertificate = (text) => {
    const index = text.lastIndexOf(',');
    if (index < 0) return { name: text };
    return { name: text.slice(0, index).trim(), issuer: text.slice(index + 1).trim() };
};

const profile = (network, url, username) => (url ? compact({ network, url, username }) : null);

export function buildJsonResume() {
    const { city, country } = splitPlace(contactsData.address);
    const profiles = [
        profile('GitHub', contactsData.github, contactsData.devUsername),
        profile('LinkedIn', contactsData.linkedIn),
        profile('Stack Overflow', contactsData.stackOverflow),
        profile('Medium', contactsData.medium),
        profile('Twitter', contactsData.twitter),
    ].filter(Boolean);

    return {
        $schema: 'https://raw.githubusercontent.com/jsonresume/resume-schema/v1.0.0/schema.json',
        basics: compact({
            name: headerData.name,
            label: headerData.role,
            email: contactsData.email,
            phone: contactsData.phone,
            url: SITE_URL,
            summary: headerData.intro,
            location: compact({ city, countryCode: undefined, region: country }),
            profiles,
        }),
        work: experienceData.map((job) =>
            compact({
                name: job.company,
                position: job.jobtitle,
                startDate: toIsoDate(job.startYear),
                endDate: toIsoDate(job.endYear),
                summary: job.summary,
                highlights: job.highlights,
            })
        ),
        education: educationData.map((item) => {
            const [studyType, area] = item.course.split(/\s+in\s+/);
            return compact({
                institution: item.institution,
                studyType,
                area,
                startDate: toIsoDate(item.startYear),
                endDate: toIsoDate(item.endYear),
                score: (item.detail || '').replace(/^CGPA\s*/i, '') || undefined,
            });
        }),
        certificates: certificationsData.map(splitCertificate),
        skills: skillsData.map((group) => ({ name: group.category, keywords: group.items })),
        projects: projectsData.map((project) =>
            compact({ name: project.projectName, description: project.projectDesc, keywords: project.tags })
        ),
    };
}

export const resumeJson = () => `${JSON.stringify(buildJsonResume(), null, 2)}\n`;

// Triggers a browser download of the résumé JSON (no server involved).
export function downloadResumeJson(filename = 'miretu-jaleta.resume.json') {
    const blob = new Blob([resumeJson()], { type: 'application/json;charset=utf-8' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(href), 1000);
}
